"use client";

import { setNavatticCurrentCampaignId } from "@/helpers/navattic";
import { BuilderBlockProps } from "@/types";
import { FC, useEffect, useRef } from "react";

type NavatticBlockProps = {
  campaignId: string;
  code: string;
} & BuilderBlockProps;

export const NavatticBlock: FC<NavatticBlockProps> = ({
  campaignId,
  code,
  attributes,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const isEventFromContainer = (event: Event) => {
      const eventPath =
        typeof event.composedPath === "function" ? event.composedPath() : [];

      return eventPath.length === 0 || eventPath.includes(container);
    };

    const markActiveCampaign = (event: Event) => {
      if (!campaignId) {
        return;
      }

      if (!isEventFromContainer(event)) {
        return;
      }

      setNavatticCurrentCampaignId(campaignId);
    };

    const markActiveCampaignOnKeydown = (event: KeyboardEvent) => {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }

      markActiveCampaign(event);
    };

    container.innerHTML = code;

    // recreate injected script tags so the browser executes the Navattic embed code
    const scripts = Array.from(container.querySelectorAll("script"));

    scripts.forEach((script) => {
      const executableScript = document.createElement("script");

      for (const { name, value } of Array.from(script.attributes)) {
        executableScript.setAttribute(name, value);
      }

      executableScript.text = script.text;
      script.replaceWith(executableScript);
    });

    // listen for clicks or keyboard interactions on the container and mark the campaign as active
    container.addEventListener("click", markActiveCampaign, true);
    container.addEventListener("focusin", markActiveCampaign, true);
    container.addEventListener("keydown", markActiveCampaignOnKeydown, true);

    return () => {
      container.removeEventListener("click", markActiveCampaign, true);
      container.removeEventListener("focusin", markActiveCampaign, true);
      container.removeEventListener(
        "keydown",
        markActiveCampaignOnKeydown,
        true,
      );
      container.innerHTML = "";
    };
  }, [campaignId, code]);

  return (
    <div
      {...attributes}
      ref={containerRef}
      data-demo-campaign-id={campaignId}
    />
  );
};
